import React, { useState, useEffect, useRef } from 'react'
import { AppBarIcons } from './AppBarIcons'
import { BrandIcon } from '../BrandIcon/BrandIcon'
import { BrandIconId } from '../BrandIcon/BrandIconId.type'
import { MenuButton } from '../MenuButton/MenuButton'
import { useSetBodyScroll } from '../../lib/hooks/useSetBodyScroll'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import { AppMenu, AppMenuProps } from '../AppMenu/AppMenu'
import { SearchPanelProps } from '../SearchPanel/SearchPanel'
import { AppMenuMobile } from '../AppMenuMobile/AppMenuMobile'
import { MenuItem } from '../../types/MenuItem.type'
import { Link } from '../Link/Link'
import { ConditionalLink, ConditionalLinkProps } from '../Link/ConditionalLink'
import { PersonalizedProspectDropdown } from './PersonalizedProspectDropdown'
import { AppBarVariant } from './AppBarVariant.type'
import { LinkSize } from '../Link/LinkSize.type'
import { LinkVariant } from '../../types/LinkVariant.type'
import { UserStatus } from '../../types/UserStatus.enum'
import { useAppContext } from '../../context/useAppContext'
import { makeUrlRelative } from '../../lib/CfgApi/makeUrlRelative'
import './style.module.scss'
import { setLocalUserStatus } from '../../lib/helpers/userStatusStorage'

export type AppBarProps = {
  variant?: AppBarVariant
  logoHref: string
  /** links are shown in on the top right */
  links?: Omit<ConditionalLinkProps, 'userStatus'>[]
  stickyHeader?: boolean
  analyticsName?: string
  menu?: Omit<AppMenuProps, 'searchPanel'>
  mobileBottomMenuItems: MenuItem[]
  searchPanel?: SearchPanelProps
  testId?: string
}

const WINDOW_SIZE_TABLET = 767

export const AppBar = (props: AppBarProps) => {
  const {
    mobileBottomMenuItems,
    logoHref,
    links,
    stickyHeader = false,
    searchPanel,
    variant,
    analyticsName,
    testId
  } = props
  const [isOpen, setIsOpen] = useState(false)
  const appBarRef = useRef<HTMLDivElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const mobileNavSectionRef = useRef<HTMLDivElement>(null)
  const atb = new AnalyticsTagBuilder()
    .setDefaultName('AppBar')
    .setAnalyticsName(analyticsName)

  const [dimensions, setDimensions] = useState({
    width: appBarRef.current?.clientWidth,
    height: appBarRef.current?.clientWidth
  })

  const { state: appState } = useAppContext()

  const setBodyScrollLock = useSetBodyScroll(false)

  const closeMenu = () => {
    setIsOpen(false)
    setBodyScrollLock(false)
  }

  const openMenu = () => {
    setIsOpen(true)
    setBodyScrollLock(true)
  }

  const handleMenuButtonClick = () => {
    if (isOpen) {
      closeMenu()
      return
    }
    openMenu()
  }

  useEffect(() => {
    const root = document.body
    if (isOpen) {
      root.classList.add('is-appMobileMenuOpen')
    } else {
      root.classList.remove('is-appMobileMenuOpen')
    }
    return () => root.classList.remove('is-appMobileMenuOpen')
  }, [isOpen])

  const onResize = () => {
    setDimensions({
      width: appBarRef.current?.clientWidth,
      height: appBarRef.current?.clientHeight
    })

    if (dimensions.width && dimensions.width >= WINDOW_SIZE_TABLET) {
      closeMenu()
    }
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeMenu()
      return
    }
    if (event.key === 'Tab') {
      // trap focus
      if (!isOpen || !mobileNavSectionRef.current) return
      const items =
        mobileNavSectionRef.current.querySelectorAll<HTMLElement>('button, a')
      const lastFocusable = items[items.length - 1]
      if (event.shiftKey) {
        if (document.activeElement === menuButtonRef.current) {
          event.preventDefault()
          lastFocusable.focus()
        }
      } else if (document.activeElement === lastFocusable) {
        event.preventDefault()
        menuButtonRef.current?.focus()
      }
    }
  }

  useEffect(() => {
    window.addEventListener('resize', onResize)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onResize, handleKeyDown])

  return (
    <>
      <AppBarIcons />
      <header
        className={`AppBar${stickyHeader ? ' u-height' : ''}`}
        {...atb.dataTargetAnalyticAttribute()}
        ref={appBarRef}
      >
        <div className={`${stickyHeader ? 'AppBar--sticky' : ''}`}>
          <div className="AppBar-topSection u-bgColorWhite">
            <div className="u-sizeConstrained">
              <div className="u-flex u-flexAlignItemsCenter u-flexJustifyBetween u-sm-paddingRight0">
                {variant !== AppBarVariant.CAMPAIGN_PAGE && (
                  <MenuButton
                    className="u-lg-hidden"
                    open={isOpen}
                    onClick={handleMenuButtonClick}
                    ref={menuButtonRef}
                    testId={testId ? `${testId}-menu-button` : undefined}
                  />
                )}
                <div className="u-flex u-sm-flexAlignSelfCenter">
                  <a
                    href={logoHref}
                    {...atb.dataAnalyticAttribute('AmpLog')}
                    data-testid={testId ? `${testId}-logo` : null}
                  >
                    <BrandIcon
                      className="AppBar-logoDesktop u-sm-hidden u-md-hidden"
                      icon={BrandIconId.AMPF_LOGO}
                      style={{
                        width: '150px',
                        height: '52px',
                        fill: '#003f6b'
                      }}
                      spriteUrl={''}
                      ariaLabel="Ameriprise Financial"
                      testId={testId ? `${testId}-mobile` : undefined}
                    />
                    <BrandIcon
                      className="AppBar-logoMobile u-lg-hidden"
                      icon={BrandIconId.AMPF_LOGO}
                      style={{
                        width: '125px',
                        height: '44px',
                        fill: '#003f6b'
                      }}
                      spriteUrl={''}
                      ariaLabel={'Ameriprise Financial'}
                      testId={testId ? `${testId}-large` : undefined}
                    />
                  </a>
                </div>
                <div className="AppHeader-topRightSection">
                  <div className="u-md-flexRow u-lg-flexRow">
                    <ul className="u-flex u-flexAlignItemsCenter u-flexJustifyCenter">
                      {variant !== AppBarVariant.CAMPAIGN_PAGE && (
                        <>
                          <li className="u-hidden u-lg-block u-paddingHoriz2gu">
                            <a
                              className="Link u-marginLeft1gu u-textPrimary u-linkClean u-flex u-flexJustifyCenter u-flexAlignItemsCenter"
                              href={makeUrlRelative(
                                'https://www.ameriprise.com/privacy-security-fraud/online-security-guarantee'
                              )}
                              data-qa="OnlineSecurityURL"
                              {...atb.dataAnalyticAttribute(
                                'OnlineSecurityGuarantee'
                              )}
                            >
                              <span
                                className="adel-material-symbols-16 u-textColorActionBlue u-paddingRight4gu"
                                aria-hidden="true"
                              >
                                security
                              </span>{' '}
                              Online security guarantee
                            </a>
                          </li>
                          {appState.ampUserStatus ===
                            UserStatus.CLIENT_ACTIVE && (
                            <>
                              <li className="u-hidden u-lg-block u-paddingHoriz2gu u-borderLeft">
                                <a
                                  className="Link u-textPrimary u-linkClean"
                                  href={makeUrlRelative(
                                    'https://www.ameriprise.com/secure/help'
                                  )}
                                  {...atb.dataAnalyticAttribute('Help')}
                                >
                                  Help
                                </a>
                              </li>
                              {appState.userData?.data?.client?.user
                                ?.clFirstNm && (
                                <li className="u-hidden u-lg-block u-paddingHoriz2gu u-borderLeft u-textColorActionBlue">
                                  Hi,{' '}
                                  <Link
                                    className="u-textUppercase"
                                    url={makeUrlRelative(
                                      'https://www.ameriprise.com/secure/profile'
                                    )}
                                    name={
                                      appState.userData.data.client.user
                                        .clFirstNm
                                    }
                                    analyticsName={atb.dataAnalyticAttributeValue(
                                      'Profile'
                                    )}
                                    testId={
                                      testId
                                        ? `${testId}-profile-link`
                                        : undefined
                                    }
                                  />
                                </li>
                              )}
                            </>
                          )}
                          {appState.ampUserStatus !==
                            UserStatus.CLIENT_ACTIVE && (
                            <li className="u-hidden u-lg-block u-paddingHoriz2gu u-borderLeft">
                              <a
                                className="Link u-textPrimary u-linkClean"
                                href={makeUrlRelative(
                                  'https://www.ameriprise.com/customer-service'
                                )}
                                {...atb.dataAnalyticAttribute(
                                  'CustomerService'
                                )}
                              >
                                Customer Service
                              </a>
                            </li>
                          )}
                        </>
                      )}
                      {links?.map(
                        (
                          link: Omit<ConditionalLinkProps, 'userStatus'>,
                          index: number
                        ) => (
                          <li key={link.url}>
                            {appState.ampUserStatus === UserStatus.PROSPECT &&
                            link.variant === LinkVariant.PRIMARY &&
                            link.name === 'Find an Advisor' &&
                            appState.advisorData?.name &&
                            (appState.advisorData?.url ||
                              appState.advisorData?.email ||
                              appState.advisorData?.phonenumber) ? (
                              <PersonalizedProspectDropdown
                                advisorData={appState.advisorData}
                                className="u-marginLeft4gu u-sm-hidden u-md-hidden"
                                testId={
                                  testId
                                    ? `${testId}-personalized-prospect-dropdown`
                                    : undefined
                                }
                              />
                            ) : (
                              <ConditionalLink
                                {...link}
                                userStatus={appState.ampUserStatus!}
                                className={`u-marginLeft4gu ${
                                  link.className || ''
                                }`}
                                href={makeUrlRelative(link.url)}
                                analyticsName={link.analyticsName}
                                size={LinkSize.SMALL}
                                testId={
                                  testId ? `${testId}-link-${index}` : undefined
                                }
                                onClick={() =>
                                  link.name === 'Log Out'
                                    ? setLocalUserStatus(
                                        UserStatus.CLIENT_EXPIRED
                                      )
                                    : undefined
                                }
                              />
                            )}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {appState.menuItems && (
            <AppMenu
              className="u-hidden u-lg-block"
              menuItems={appState.menuItems}
              searchPanel={searchPanel}
              stickyLinks={links}
            />
          )}
          <AppMenuMobile
            userStatus={appState.ampUserStatus}
            className="u-block u-lg-hidden"
            searchPanel={searchPanel}
            menuItems={appState.menuItems?.filter(
              menuItem => menuItem.name !== 'Home'
            )}
            mobileBottomMenuItems={mobileBottomMenuItems}
            isOpen={isOpen}
            testId={testId}
            mobileNavSectionRef={mobileNavSectionRef}
            advisorData={appState.advisorData}
            userData={appState.userData}
          />
        </div>
      </header>
    </>
  )
}
